import { BonusContext, IBonusScene, IConfiguration, INumberFormatter } from '@cgs/common';
import { BonusResponse, BonusRound } from '@cgs/data-access';
import { Func3, CancellationToken } from '@cgs/shared';
import { BonusGame } from './bonus_game';
import { RulesBasedBonusMessageResolver } from './resolvers/rules_based_bonus_message_resolver';

export interface BonusResponseWithRules extends BonusResponse {
  currentRound: BonusRoundWithRules | null;
  nextRound: BonusRoundWithRules | null;
}

export interface BonusRoundWithRules extends BonusRound {
  rules: IRoundRules | null;
}

export const enum RuleTypeLogic {
  WinLose = 'WinLose',
}

export const enum RulePropertyGenerator {
  WinLoseIndexes = 'winLoseIndexes',
}

export interface IRoundRules {
  messageTypeLogic: {
    name: RuleTypeLogic;
    winTypes: string[];
    failTypes: string[];
  };
  propertyGenerators: RulePropertyGenerator[];
}

export class BonusGameWithRules extends BonusGame {
  private readonly _configuration: IConfiguration;

  constructor(
    bonusScene: IBonusScene,
    configuration: IConfiguration,
    bonusContext: BonusContext,
    updateBonusFinishPropertiesWithCurrent: Func3<
      BonusResponse,
      Map<string, any>,
      Map<string, any>,
      void
    > | null,
    gameType: string,
    cancellationToken: CancellationToken,
    numberFormatter: INumberFormatter
  ) {
    super(
      bonusScene,
      bonusContext,
      updateBonusFinishPropertiesWithCurrent,
      gameType,
      cancellationToken,
      numberFormatter
    );

    this._configuration = configuration;

    // Replace the default message resolver with the rules-based one
    this.bonusMessageResolver = new RulesBasedBonusMessageResolver(
      bonusContext,
      updateBonusFinishPropertiesWithCurrent,
      configuration,
      numberFormatter
    );
  }

  get configuration(): IConfiguration {
    return this._configuration;
  }

  async processMessage(bonusResponse: BonusResponse): Promise<void> {
    // Custom message processing logic can be added here
    // For now, use the standard processing from the parent class
    await this.onResponse(bonusResponse);
  }

  /**
   * Validates that the bonus response contains the expected rules structure
   */
  private validateRulesStructure(bonusResponse: BonusResponse): boolean {
    if (!bonusResponse.currentRound) {
      return false;
    }

    const roundWithRules = bonusResponse.currentRound as any;
    if (!roundWithRules.rules) {
      // Rules are optional, so this is not an error
      return true;
    }

    const rules = roundWithRules.rules;

    // Validate messageTypeLogic structure if present
    if (rules.messageTypeLogic) {
      const logic = rules.messageTypeLogic;
      if (!logic.name || typeof logic.name !== 'string') {
        console.warn('Invalid messageTypeLogic.name in bonus rules');
        return false;
      }
    }

    // Validate propertyGenerators structure if present
    if (rules.propertyGenerators) {
      if (!Array.isArray(rules.propertyGenerators)) {
        console.warn('Invalid propertyGenerators in bonus rules - must be array');
        return false;
      }
    }

    return true;
  }

  start(bonusResponse: BonusResponse): void {
    // Validate rules structure before starting
    if (!this.validateRulesStructure(bonusResponse)) {
      console.warn('Invalid rules structure detected, falling back to default behavior');
    }

    // Call parent start method
    super.start(bonusResponse);
  }

  async onResponse(bonusResponse: BonusResponse): Promise<void> {
    // Validate rules structure for each response
    if (!this.validateRulesStructure(bonusResponse)) {
      console.warn(
        'Invalid rules structure detected in response, falling back to default behavior'
      );
    }

    // Call parent onResponse method
    await super.onResponse(bonusResponse);
  }
}
